'use strict';

import { observable, configure, action, observe, makeObservable } from "mobx";
configure({enforceActions: 'always'});

import {States} from "@uw-it-sis/lib-react/lib/AppConstants";
import termStore from '@uw-it-sis/lib-react/lib/TermStore';
import {getAggregateState} from '@uw-it-sis/lib-react/lib/StateUtils';
import TermUtils from "@uw-it-sis/lib-react/lib/TermUtils";

import planNavigationStore from "../plan/PlanNavigationStore";

class ViewIdentifierStore {
    state = States.initial;

    constructor() {
        makeObservable(this, {
            state: observable,
            mount: action,
            checkDataLoadStates: action
        });

        this.dataLoadStates = {
            termStore: States.initial,
            planNavigation: States.initial,
            internalProcessing: States.initial
        };
    }

    mount() {
        // Set initial internal states to pending
        this.dataLoadStates = {
            termStore: States.pending,
            planNavigation: States.pending,
            internalProcessing: States.pending
        };

        this.checkDataLoadStates();

        this.termStoreDisposer = observe(termStore, "state", function(change) {
            this.dataLoadStates.termStore = change.newValue;
            this.checkDataLoadStates();
        }.bind(this));

        termStore.load(['publishedTerms','futureTerms','currentPlanableTerm']);

        this.planNavigationDisposer = observe(planNavigationStore, "state", function(change) {
            this.dataLoadStates.planNavigation = change.newValue;

            this.checkDataLoadStates();
        }.bind(this));

        planNavigationStore.loadData();

        this.dataLoadStates.internalProcessing = States.done;

        this.checkDataLoadStates();
    }

    unmount() {
        this.termStoreDisposer();
        this.planNavigationDisposer();
    }

    /**
     * Check aggregate data load states and update the overall state if necessary.
     */
    checkDataLoadStates() {
        this.state = getAggregateState(this.dataLoadStates);
    }

    /**
     * Pass-thru to PublishedTermStore
     */
    isPublished(termId) {
        let state = false;
        for (let term of termStore.get('publishedTerms')) {
            if (term.id === termId) state = true;
        }
        return state;
    }

    /**
     * Pass-thru to FutureTermsStore
     */
    getFutureTerms() {
        return termStore.get('futureTerms');
    }

    /**
     * pass-thru to CurrentPlanableTermStore
     */
    getCurrentPlanableTerm() {
        return termStore.get('currentPlanableTerm');
    }

    /**
     * Gets a list of years from the future term store. No duplicates
     * @return {*}
     */
    getFutureYears() {
        const futureTerms = this.getFutureTerms();
        return futureTerms.map(term => term.year).filter((x, i, a) => a.indexOf(x) === i);
    }

    /**
     * Builds a list of planned years for navigating. Combines the list of plan years from
     * user academic history, plus the years from the future term store. Duplicates removed
     * just in case.
     * @return {*} - list of years as strings
     */
    getPlanNavigation() {
        // Get the list of year from academic record, making them strings for comparision
        const planNavigation = planNavigationStore.getData().map(year => year.toString());
        // Get list of future years
        const futureYears = this.getFutureYears();
        // Concat both lists
        const combinedYears = planNavigation.concat(futureYears);
        // Return years list with duplicates removed.
        return combinedYears.filter((x, i, a) => a.indexOf(x) === i);
    }

    /**
     * Returns the last year from FutureTermsStore. If the last term is 20263 (Summer 2026),
     * then the academic year is 2025-2026, returns 2025
     * @return {number}
     */
    getLastFutureTermYear() {
        const futureTerms = this.getFutureTerms();
        return parseInt(futureTerms[futureTerms.length - 1].year) - 1;
    }

    /**
     * Returns the first year within the user's plan provided by the PlanNavigationStore
     * @return {number}
     */
    getFirstBeginningYear() {
        const planNavigation = planNavigationStore.getData();

        let result;

        if (planNavigation.length === 0) {
            // if the user has no plan navigation history from the api, use the current planable term year
            result = this.getCurrentPlanableTerm().year;
        }
        else {
            result = parseInt(planNavigation[0]);
        }

        return result;
    }

    /**
     * Returns the next navigable academic year in user's plan from the current displayed year
     * @param year - current year being viewed
     * @return {string|null} - returns an academic year code if there is one to be navigated to,
     * null if there is not (last year of the plan/future terms)
     */
    getPlanNavigationNext(year) {
        const viewYear = year.toString();
        let planNavigation = this.getPlanNavigation();
        const maxYear = this.getLastFutureTermYear().toString();
        let tempYear, tempTermId;

        // if the current year is at the max, there is no next year to navigate to
        if ( viewYear === maxYear ) {
            return null;
        } else {
            // Currently viewed year is not in the list of planned years
            if (planNavigation.indexOf(viewYear) < 0) {
                // Add year to the list and sort so that we can provide the next viewable year for navigation
                planNavigation.push(viewYear);
                planNavigation.sort();
            }

            // If the currently viewed year is the last item in the list of planned years...
            if (planNavigation.indexOf(viewYear) + 1 === planNavigation.length) {
                // ...then manually increment to next year
                tempTermId = `${parseInt(viewYear) + 1}4`;
            } else {
                // ...else return the next item from the list of planned years
                tempYear = planNavigation[(planNavigation.indexOf(viewYear) + 1)];
                tempTermId = `${tempYear}4`;
            }

            // return converted plan year to an academic year code
            return TermUtils.getAcademicYearCode(tempTermId, TermUtils.SIZE.MEDIUM);
        }
    }

    /**
     *
     * @param year
     * @return {string|null}
     */
    getPlanNavigationPrev(year) {
        const viewYear = year.toString();
        let planNavigation = this.getPlanNavigation();
        let tempYear, tempTermId;

        // Currently viewed year is not in the list of planned years
        if (planNavigation.indexOf(viewYear) < 0) {
            // Add year to the planned years list and sort so that we can provide the previous viewable year for navigation
            planNavigation.push(viewYear);
            planNavigation.sort();
        }

        // If the currently viewed  year is the first item in the list of planned years...
        if (planNavigation.indexOf(viewYear) === 0) {
            // ...then disable navigating to previous year by returning null
            return null;
        } else {
            // ...else return the previous item from the list of planned years
            tempYear = planNavigation[(planNavigation.indexOf(viewYear) - 1)];
            tempTermId = `${tempYear}4`;

            // return converted plan year to an academic year code
            return TermUtils.getAcademicYearCode(tempTermId, TermUtils.SIZE.MEDIUM);
        }

    }

    /**
     * Check to see if year span string is properly formatted. (e.g. 2020-21) and is within
     * our defined first beginning year and last future term year
     * @param identifier - string from the url
     * @return {boolean}
     */
    isValidAcademicYear(identifier) {
        const minYear = this.getFirstBeginningYear();
        const maxYear = this.getLastFutureTermYear();
        const academicYearRegex = /^(\d{2})(\d{2})-(\d{2})$/;
        const academicYearMatch = identifier.match(academicYearRegex);

        let year, isConsecutive;
        if (academicYearMatch) {
            year = parseInt(`${academicYearMatch[1]}${academicYearMatch[2]}`);

            // Handle turn of the century, if year span ends in "00", use 100 in math
            isConsecutive = (parseInt(academicYearMatch[3] === "00" ? 100 : academicYearMatch[3]) - parseInt(academicYearMatch[2])) === 1;
        }

        /**
         * Academic year is valid if:
         * - match list is defined
         * - match list length is 4
         * - ending 2 digits of the span are consecutive
         * - first year of span is less than/equal to max year (end of future terms)
         * - first year of span is greater than/equal to min year (current year minus 100)
         */
        return (
            academicYearMatch &&
            academicYearMatch.length === 4 &&
            isConsecutive &&
            year <= maxYear &&
            year >= minYear
        );
    }
}

export default new ViewIdentifierStore();